import React, { useState, useEffect, useRef } from 'react';
import './ChefDishManager.css';

const ChefDishManager = () => {
  const [cities, setCities] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [selectedCity, setSelectedCity] = useState('toronto');
  const [selectedChef, setSelectedChef] = useState('');
  const [dishData, setDishData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    prepTime: '',
    servings: '',
    difficulty: 'medium',
    dietary: [],
    ingredients: '',
    allergens: ''
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);

  // Use the same cities as ChefWidget
  const CITIES = [
    { id: 'toronto', name: 'Toronto' },
    { id: 'new-york', name: 'New York' },
    { id: 'boston', name: 'Boston' },
    { id: 'chicago', name: 'Chicago' },
    { id: 'new-orleans', name: 'New Orleans' },
    { id: 'miami', name: 'Miami' },
    { id: 'austin', name: 'Austin' },
    { id: 'dallas', name: 'Dallas' },
    { id: 'houston', name: 'Houston' }
  ];

  // Load chefs when city changes
  useEffect(() => {
    if (selectedCity) {
      loadChefsForCity(selectedCity);
    } else {
      setChefs([]);
      setSelectedChef('');
    }
  }, [selectedCity]);

  const loadChefsForCity = async (city) => {
    try {
      const response = await fetch(`/api/chefs?city=${city}&status=approved,live`);
      const data = await response.json();
      setChefs(data.results || []);
    } catch (error) {
      console.error('Error loading chefs:', error);
    }
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = selectedFiles.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
      return isValidType && isValidSize;
    });

    if (validFiles.length !== selectedFiles.length) {
      alert('Some files were skipped. Only image files under 5MB are allowed.');
    }

    setFiles(prev => [...prev, ...validFiles].slice(0, 10)); // Max 10 images per dish
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadDishImages = async () => {
    if (files.length === 0 || !selectedChef || !dishData.name) {
      alert('Please select a chef, enter dish name, and select images.');
      return;
    }

    setUploading(true);
    setUploadProgress({});
    const results = [];

    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress(prev => ({ ...prev, [i]: 'uploading' }));
        
        try {
          const result = await uploadToCloudflareImages(files[i], i);
          results.push({
            file: files[i],
            result: result,
            index: i
          });
          setUploadProgress(prev => ({ ...prev, [i]: 'completed' }));
        } catch (error) {
          setUploadProgress(prev => ({ ...prev, [i]: 'error' }));
          console.error(`Upload failed for file ${i}:`, error);
        }
      }

      // Save dish data to database
      await saveDishToDatabase(results);
      
      setUploadedImages(results);
      alert(`Successfully created dish "${dishData.name}" for ${selectedChef} with ${results.length} images!`);
      
      // Reset form
      resetForm();
    } finally {
      setUploading(false);
    }
  };

  const uploadToCloudflareImages = async (file, index) => {
    try {
      const response = await fetch('/api/images/direct-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || 'Boodgy'}`
        },
        body: JSON.stringify({
          metadata: {
            type: 'dishes',
            category: 'gourmet-dishes',
            folder: 'dishes',
            fileName: file.name,
            uploadedAt: new Date().toISOString(),
            fileSize: file.size,
            fileType: file.type,
            uploadedBy: 'chef-dish-manager',
            purpose: 'dish-photo',
            chefName: selectedChef,
            city: selectedCity,
            dishName: dishData.name,
            dishCategory: dishData.category
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadURL } = await response.json();

      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(uploadURL, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      const uploadResult = await uploadResponse.json();
      console.log('ChefDishManager upload result:', uploadResult);
      
      // Cloudflare Images returns { id: "image-id", filename: "filename.jpg" }
      // We need to construct the delivery URL
      if (uploadResult.id) {
        return {
          id: uploadResult.id,
          filename: uploadResult.filename || file.name,
          url: `https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/${uploadResult.id}/public`
        };
      } else {
        throw new Error('Upload response missing image ID');
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const saveDishToDatabase = async (imageResults) => {
    try {
      // Find the chef ID from the selected chef name
      const chef = chefs.find(c => c.name === selectedChef);
      if (!chef) {
        throw new Error('Chef not found');
      }

      const dishRecord = {
        chef_id: chef.id, // Use the chef ID as-is (could be string UUID or number)
        name: dishData.name,
        description: dishData.description,
        price_cents: Math.round((parseFloat(dishData.price) || 0) * 100), // Convert to cents
        currency: 'USD',
        categories: dishData.category,
        tags: JSON.stringify({
          prep_time: dishData.prepTime,
          servings: parseInt(dishData.servings) || 1,
          difficulty: dishData.difficulty,
          dietary: dishData.dietary,
          ingredients: dishData.ingredients,
          allergens: dishData.allergens,
          city: selectedCity,
          chef_name: selectedChef,
          images: imageResults.map(img => ({
            id: img.result.id,
            url: img.result.url,
            filename: img.result.filename
          }))
        }),
        active: 1,
        created_at: new Date().toISOString()
      };

      const response = await fetch('/api/admin/dishes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || 'Boodgy'}`
        },
        body: JSON.stringify(dishRecord)
      });

      if (!response.ok) {
        throw new Error('Failed to save dish to database');
      }

      return await response.json();
    } catch (error) {
      console.error('Database save error:', error);
      throw error;
    }
  };

  const resetForm = () => {
    setDishData({
      name: '',
      description: '',
      price: '',
      category: '',
      prepTime: '',
      servings: '',
      difficulty: 'medium',
      dietary: [],
      ingredients: '',
      allergens: ''
    });
    setFiles([]);
    setUploadProgress({});
    setUploadedImages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDietaryChange = (diet) => {
    setDishData(prev => ({
      ...prev,
      dietary: prev.dietary.includes(diet) 
        ? prev.dietary.filter(d => d !== diet)
        : [...prev.dietary, diet]
    }));
  };

  return (
    <div className="chef-dish-manager">
      <div className="manager-header">
        <h2>Chef-Dish Management System</h2>
        <p>Command Central: Assign dishes to chefs by city</p>
      </div>

      {/* City and Chef Selection */}
      <div className="selection-section">
        <div className="city-pills">
          {CITIES.map(city => (
            <button
              key={city.id}
              className={`city-pill ${selectedCity === city.id ? 'active' : ''}`}
              onClick={() => setSelectedCity(city.id)}
            >
              {city.name}
            </button>
          ))}
        </div>

        {selectedCity && (
          <div className="chef-selection">
            <h4>Select Chef</h4>
            <div className="chef-pills">
              {chefs.map(chef => (
                <button
                  key={chef.id}
                  className={`chef-pill ${selectedChef === chef.name ? 'active' : ''}`}
                  onClick={() => setSelectedChef(chef.name)}
                >
                  {chef.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Dish Information */}
      {selectedChef && (
        <div className="dish-info-section">
          <h3>Dish Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Dish Name *</label>
              <input
                type="text"
                value={dishData.name}
                onChange={(e) => setDishData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Lobster Thermidor"
                className="dish-name-input"
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select
                value={dishData.category}
                onChange={(e) => setDishData(prev => ({ ...prev, category: e.target.value }))}
                className="category-select"
              >
                <option value="">Select category...</option>
                <option value="appetizer">Appetizer</option>
                <option value="main-course">Main Course</option>
                <option value="dessert">Dessert</option>
                <option value="beverage">Beverage</option>
                <option value="side-dish">Side Dish</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={dishData.description}
              onChange={(e) => setDishData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the dish, cooking method, flavors..."
              className="description-textarea"
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={dishData.price}
                onChange={(e) => setDishData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="25.00"
                className="price-input"
              />
            </div>
            <div className="form-group">
              <label>Prep Time</label>
              <input
                type="text"
                value={dishData.prepTime}
                onChange={(e) => setDishData(prev => ({ ...prev, prepTime: e.target.value }))}
                placeholder="30 minutes"
                className="prep-time-input"
              />
            </div>
            <div className="form-group">
              <label>Servings</label>
              <input
                type="number"
                value={dishData.servings}
                onChange={(e) => setDishData(prev => ({ ...prev, servings: e.target.value }))}
                placeholder="4"
                className="servings-input"
              />
            </div>
            <div className="form-group">
              <label>Difficulty</label>
              <select
                value={dishData.difficulty}
                onChange={(e) => setDishData(prev => ({ ...prev, difficulty: e.target.value }))}
                className="difficulty-select"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Dietary Options</label>
            <div className="dietary-options">
              {['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'keto', 'paleo', 'halal', 'kosher'].map(diet => (
                <label key={diet} className="dietary-checkbox">
                  <input
                    type="checkbox"
                    checked={dishData.dietary.includes(diet)}
                    onChange={() => handleDietaryChange(diet)}
                  />
                  <span>{diet.charAt(0).toUpperCase() + diet.slice(1).replace('-', ' ')}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Key Ingredients</label>
              <input
                type="text"
                value={dishData.ingredients}
                onChange={(e) => setDishData(prev => ({ ...prev, ingredients: e.target.value }))}
                placeholder="lobster, cream, brandy, herbs..."
                className="ingredients-input"
              />
            </div>
            <div className="form-group">
              <label>Allergens</label>
              <input
                type="text"
                value={dishData.allergens}
                onChange={(e) => setDishData(prev => ({ ...prev, allergens: e.target.value }))}
                placeholder="shellfish, dairy..."
                className="allergens-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* Image Upload */}
      {selectedChef && dishData.name && (
        <div className="image-upload-section">
          <h3>Dish Images</h3>
          
          <div className="file-selection">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
              className="file-input"
            />
            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="select-images-btn"
            >
              Select Dish Images (up to 10)
            </button>
          </div>

          {/* File Preview */}
          {files.length > 0 && (
            <div className="file-preview">
              <h4>Selected Images ({files.length}/10)</h4>
              <div className="file-grid">
                {files.map((file, index) => (
                  <div key={index} className="file-item">
                    <div className="file-preview-img">
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={file.name}
                        className="preview-image"
                      />
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                    </div>
                    <div className="file-status">
                      {uploadProgress[index] === 'uploading' && (
                        <span className="status uploading">⏳ Uploading...</span>
                      )}
                      {uploadProgress[index] === 'completed' && (
                        <span className="status completed">✅ Complete</span>
                      )}
                      {uploadProgress[index] === 'error' && (
                        <span className="status error">❌ Error</span>
                      )}
                    </div>
                    <button 
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                      className="remove-btn"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {files.length > 0 && (
            <div className="upload-controls">
              <button 
                onClick={uploadDishImages}
                disabled={uploading || !dishData.name}
                className="create-dish-btn"
              >
                {uploading ? 'Creating Dish...' : `Create "${dishData.name}" for ${selectedChef}`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {uploadedImages.length > 0 && (
        <div className="success-section">
          <h3>✅ Dish Created Successfully!</h3>
          <p><strong>{dishData.name}</strong> has been added to <strong>{selectedChef}</strong>'s repertoire in <strong>{selectedCity}</strong></p>
          <p>Images uploaded: {uploadedImages.length}</p>
        </div>
      )}
    </div>
  );
};

export default ChefDishManager;
